// DEV-07 通道設定總表：CH1–CHn 並排，一列一個設定項目，值跟多數通道不一樣的格子標出來。
import type { DeviceSettings, SettingItem } from '../../lib/deviceApi';
import { itemLabel, kinioLabel } from '../../lib/kinioLabel';
import { tierOf, type Tier } from './settingsTiers';

export interface MatrixRow {
  key: string;
  label: string;
  /** 層（照第一個有這個項目的通道；同機種各路一樣）——篩選晶片用 */
  tier: Tier;
  /** 跟 chans 同順序；那一路沒有這個項目 = undefined */
  cells: (SettingItem | undefined)[];
  /** 各路的值不全相同 */
  differs: boolean;
  /** 多數通道的值（JSON 字串）；跟它不同的格子要標 */
  common: string;
}

export interface MatrixSection {
  name: string;
  label: string;
  rows: MatrixRow[];
}

const j = (v: unknown) => JSON.stringify(v ?? null);

/** 出現最多次的值（同票取先出現的）。 */
export function commonValue(values: string[]): string {
  const n = new Map<string, number>();
  let best = values[0] ?? 'null';
  for (const v of values) {
    const c = (n.get(v) ?? 0) + 1;
    n.set(v, c);
    if (c > (n.get(best) ?? 0)) best = v;
  }
  return best;
}

/** 各通道的設定表單 → 區段 / 列（順序照第一個有那個項目的通道）。指令（button）不列。 */
export function buildMatrix(chans: string[], settings: Record<string, DeviceSettings | undefined>): MatrixSection[] {
  const sections: MatrixSection[] = [];
  const rowOf = new Map<string, MatrixRow>();
  chans.forEach((aid, ci) => {
    for (const sec of settings[aid]?.sections ?? []) {
      let s = sections.find((x) => x.name === sec.name);
      if (!s) sections.push((s = { name: sec.name, label: kinioLabel(sec.label_id, sec.label), rows: [] }));
      for (const it of sec.clusters.flatMap((c) => c.items)) {
        if (it.control === 'button') continue;
        let r = rowOf.get(it.key);
        if (!r) {
          r = { key: it.key, label: itemLabel(it), tier: tierOf(it), cells: chans.map(() => undefined), differs: false, common: 'null' };
          rowOf.set(it.key, r);
          s.rows.push(r);
        }
        r.cells[ci] = it;
      }
    }
  });
  for (const r of rowOf.values()) {
    const vals = r.cells.filter((c): c is SettingItem => !!c).map((c) => j(c.value));
    r.common = commonValue(vals);
    r.differs = new Set(vals).size > 1;
  }
  return sections.filter((s) => s.rows.length > 0);
}

/** 這一格的值跟多數通道不同。 */
export const isOdd = (r: MatrixRow, c: SettingItem | undefined): boolean => !!c && r.differs && j(c.value) !== r.common;

/** 這一格能不能直接改（開關 / 選單 / 數字 / 文字；自訂編輯器與唯讀不行）。 */
export const cellEditable = (c: SettingItem | undefined): boolean =>
  !!c &&
  c.can_write &&
  !c.destructive &&
  (c.control === 'switch' || c.control === 'select' || c.control === 'slider' || c.control === 'stepper' || c.control === 'text');

/** 篩選晶片：只留勾選的層；沒有列的區段拿掉。 */
export function filterMatrix(sections: MatrixSection[], tiers: ReadonlySet<Tier>): MatrixSection[] {
  return sections.map((s) => ({ ...s, rows: s.rows.filter((r) => tiers.has(r.tier)) })).filter((s) => s.rows.length > 0);
}
